import { bootAuth, type AuthHub } from "./auth/index";
import type { AuthUser } from "./auth/types";
import type { Grade, Mode, Op, ScoreBreakdown, Settings, SubmitPayload, TrailEvent, TrailSpec } from "./types";
import { GRADE_LABELS, LEVEL_ART, OP_LABELS } from "./game/config";
import { makeTrail } from "./game/trail";
import { Board } from "./game/board";
import * as audio from "./audio";
import {
  DEFAULT_SETTINGS,
  applyDomSettings,
  getStars,
  loadStore,
  makeClassCode,
  newId,
  saveStore,
  setStar,
  unlockedLevel,
  type FTStore,
} from "./storage";
import { fmtClock, fmtTime, gradeTiles, headerHtml, starsHtml, toast, esc } from "./ui";
import { utcDateISO } from "./rng";
import {
  SCOUTS,
  LANTERNS,
  PACKS,
  STAMPS,
  activeScout,
  buyWithMarks,
  canUnlock,
  classroomLocked,
  ensureLocker,
  grantDeedUnlocks,
  isUnlocked,
  lanternGlow,
  payoutMarks,
  scoutById,
  type ScoutId,
} from "./cosmetics";

type Route =
  | "home"
  | "grade"
  | "campaign"
  | "play"
  | "daily"
  | "practice"
  | "leaderboard"
  | "settings"
  | "account"
  | "desk"
  | "locker";

const root = () => document.getElementById("app") as HTMLElement;

let auth: AuthHub;
let store: FTStore;
let board: Board | null = null;
let lastSpec: TrailSpec | null = null;
let lastScore: ScoreBreakdown | null = null;
let playIntent: { mode: Mode; grade: Grade; level?: number; op?: Op } | null = null;

function user(): AuthUser | null {
  return auth?.user || null;
}

function nick(): string {
  const p = store.profiles.find((x) => x.id === store.activeProfileId);
  return p?.nickname || user()?.nickname || "Guest";
}

function route(): Route {
  const h = (location.hash || "#/").replace(/^#\/?/, "").split("?")[0];
  const first = (h.split("/")[0] || "home") as Route;
  const known: Route[] = [
    "home",
    "grade",
    "campaign",
    "play",
    "daily",
    "practice",
    "leaderboard",
    "settings",
    "account",
    "desk",
    "locker",
  ];
  return known.includes(first) ? first : "home";
}

function go(r: string) {
  location.hash = "#/" + r.replace(/^#\/?/, "");
}

function persist() {
  saveStore(store);
  applyDomSettings(store.settings);
  audio.setMuted(store.settings.mute);
}

export async function start() {
  store = loadStore();
  ensureLocker(store);
  grantDeedUnlocks(store);
  persist();
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) store.settings.reducedMotion = true;
  applyDomSettings(store.settings);
  audio.setMuted(store.settings.mute);
  auth = await bootAuth();
  window.addEventListener("hashchange", render);
  render();
}

function render() {
  board?.destroy();
  board = null;
  const r = route();
  if (store.settings.sharedDevice && !store.activeProfileId && r !== "desk" && r !== "account" && r !== "settings") {
    paintPicker();
    return;
  }
  switch (r) {
    case "grade":
      paintGrade();
      break;
    case "campaign":
      paintCampaign();
      break;
    case "play":
      paintPlay();
      break;
    case "daily":
      playIntent = { mode: "daily", grade: store.settings.grade };
      paintPlay();
      break;
    case "practice":
      paintPractice();
      break;
    case "leaderboard":
      paintBoard();
      break;
    case "settings":
      paintSettings();
      break;
    case "account":
      paintAccount();
      break;
    case "desk":
      paintDesk();
      break;
    case "locker":
      paintLocker();
      break;
    default:
      paintHome();
  }
}

function shell(routeName: Route, body: string, extraClass = ""): string {
  return `${headerHtml(user(), routeName)}
    <main class="page ${extraClass}">${body}</main>
    <footer class="foot"><span>Orangecat Digital</span> · Fact Trail · kid-safe, no ads</footer>`;
}

function paintHome() {
  const g = GRADE_LABELS[store.settings.grade];
  const L = ensureLocker(store);
  const locked = classroomLocked(store);
  const scout = scoutById(activeScout(store));
  const scoutBtn = locked
    ? `<p class="fine">Scouts are paused for class.</p>`
    : `<button class="scout-bust" data-go="locker" type="button">
        <img src="${scout.src}" alt="${esc(scout.name)}" />
        <span>Scout · ${esc(scout.name)}</span>
      </button>`;
  root().innerHTML = shell(
    "home",
    `<section class="hero">
      <div class="hero-art" style="background-image:url('assets/art/title.jpg')"></div>
      <div class="hero-card">
        ${scoutBtn}
        <h1>Fact Trail</h1>
        <p class="lede">Walk the path. Uncover the picture. Race the clock.</p>
        <p class="meta-line">Grade ${g} · change anytime · ${L.marks} Marks</p>
        <div class="cta-grid">
          <button class="btn btn-primary" data-go="campaign">Play Campaign</button>
          <button class="btn btn-warm" data-go="daily">Daily Trail</button>
          <button class="btn" data-go="practice">Practice</button>
          <button class="btn" data-go="leaderboard">Leaderboard</button>
        </div>
        <div class="mini-row">
          <button class="btn btn-ghost" data-go="play-quick">Quick Play</button>
          <button class="btn btn-ghost" data-go="grade">Change grade</button>
          <button class="btn btn-ghost" data-go="settings">Settings</button>
        </div>
      </div>
    </section>
    ${!L.firstPickDone && !locked ? firstPickHtml() : ""}`,
    "home-page"
  );
  root().querySelectorAll("[data-go]").forEach((b) =>
    b.addEventListener("click", () => {
      const v = (b as HTMLElement).dataset.go;
      if (v === "play-quick") {
        playIntent = { mode: "quick", grade: store.settings.grade };
        go("play");
      } else if (v === "campaign") {
        go("campaign");
      } else go(v || "home");
    })
  );
  bindFirstPick();
}

function paintGrade() {
  root().innerHTML = shell(
    "grade",
    `<h1>Pick a grade</h1>
    <p class="lede">We’ll remember this. Parents can change it later.</p>
    <div class="grade-grid">${gradeTiles(store.settings.grade)}</div>
    <p class="fine">K–2 use a 5×5 board. 3–5 a 6×6. 6–8 a 7×7.</p>`
  );
  root().querySelectorAll<HTMLButtonElement>(".grade-tile").forEach((btn) => {
    btn.addEventListener("click", () => {
      store.settings.grade = Number(btn.dataset.grade) as Grade;
      persist();
      go("home");
    });
  });
}

function paintCampaign() {
  const grade = store.settings.grade;
  const stars = getStars(store, grade);
  const unlocked = unlockedLevel(store, grade);
  const nodes = LEVEL_ART.slice(0, 10)
    .map((art, i) => {
      const n = i + 1;
      const lock = n > unlocked;
      const st = stars[i] || 0;
      const thumb = `assets/illustrations/${art.file}`;
      return `<button type="button" class="node${lock ? " is-lock" : ""}${st ? " is-clear" : ""}" data-level="${n}" ${lock ? "disabled" : ""}>
        <span class="node-art" style="background-image:url('${thumb}')"></span>
        <span class="node-num">${n}</span>
        <span class="node-title">${esc(art.title)}</span>
        ${lock ? `<span class="lock">Locked</span>` : starsHtml(st)}
      </button>`;
    })
    .join("");
  root().innerHTML = shell(
    "campaign",
    `<div class="row-between">
      <div>
        <h1>Campaign · Grade ${GRADE_LABELS[grade]}</h1>
        <p class="lede">Ten trails. Each one hides a new picture.</p>
      </div>
      <button class="btn btn-ghost" data-go="grade">Change grade</button>
    </div>
    <div class="campaign-map">${nodes}</div>
    <p class="fine">Clear a trail to light the next lantern.</p>`
  );
  $("[data-go='grade']")?.addEventListener("click", () => go("grade"));
  root().querySelectorAll<HTMLButtonElement>(".node").forEach((btn) => {
    btn.addEventListener("click", () => {
      playIntent = { mode: "campaign", grade, level: Number(btn.dataset.level) };
      go("play");
    });
  });
}

function paintPractice() {
  const ops = (Object.keys(OP_LABELS) as Op[])
    .map((op) => `<button class="btn op" data-op="${op}">${OP_LABELS[op]}</button>`)
    .join("");
  root().innerHTML = shell(
    "practice",
    `<h1>Practice</h1>
    <p class="lede">Untimed is optional. Hints appear after two misses. Practice scores stay off the Daily board.</p>
    <div class="grade-grid">${gradeTiles(store.settings.grade)}</div>
    <h2 class="h2">Operation</h2>
    <div class="cta-grid">${ops}</div>
    <label class="check"><input type="checkbox" id="untimed" /> Untimed trail</label>`
  );
  root().querySelectorAll<HTMLButtonElement>(".grade-tile").forEach((btn) => {
    btn.addEventListener("click", () => {
      store.settings.grade = Number(btn.dataset.grade) as Grade;
      persist();
      paintPractice();
    });
  });
  root().querySelectorAll<HTMLButtonElement>("[data-op]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const untimed = (document.getElementById("untimed") as HTMLInputElement).checked;
      playIntent = { mode: "practice", grade: store.settings.grade, op: btn.dataset.op as Op };
      (playIntent as any).untimed = untimed;
      go("play");
    });
  });
}

function paintPlay() {
  const intent = playIntent || { mode: "quick" as Mode, grade: store.settings.grade };
  const extra = store.settings.extraTime;
  const spec = makeTrail({
    mode: intent.mode,
    grade: intent.grade,
    level: intent.level ?? (intent.mode === "campaign" ? 1 : null),
    op: intent.op ?? null,
    extraTime: extra,
  });
  if ((intent as any).untimed) spec.timeLimitMs = 60 * 60 * 1000;
  lastSpec = spec;
  lastScore = null;

  root().innerHTML = `${headerHtml(user(), "play")}
    <main class="play-page">
      <div class="hud hud-journal">
        <button class="icon-btn" id="pause" aria-label="Pause">II</button>
        <div class="hud-stat"><span>Time</span><strong id="hud-time">${fmtClock(spec.timeLimitMs)}</strong></div>
        <div class="hud-stat"><span>Score</span><strong id="hud-score">0</strong></div>
        <div class="hud-stat"><span>Streak</span><strong id="hud-streak">0</strong></div>
        <button class="icon-btn" id="speak" aria-label="Read the problem">Read</button>
        <button class="icon-btn" id="mute" aria-label="Mute">${store.settings.mute ? "Muted" : "Sound"}</button>
      </div>
      <div class="play-body">
        <div class="board-wrap">
          <canvas id="board"></canvas>
          <div id="overlay" class="board-overlay"></div>
        </div>
      </div>
      <div id="combo" class="combo-host"></div>
    </main>
    <div id="pause-menu" class="modal hidden">
      <div class="modal-card">
        <h2>Paused</h2>
        <button class="btn btn-primary" id="resume">Resume</button>
        <button class="btn" id="restart">Restart trail</button>
        <button class="btn" id="quit">Quit to map</button>
        ${classroomLocked(store) ? "" : `<button class="btn" id="pm-scout">Scout</button>`}
        <button class="btn" id="pm-mute">Mute</button>
      </div>
    </div>`;

  const canvas = document.getElementById("board") as HTMLCanvasElement;
  const overlay = document.getElementById("overlay") as HTMLElement;
  const comboHost = document.getElementById("combo") as HTMLElement;

  const look = {
    scoutSrc: scoutById(activeScout(store)).src,
    glow: lanternGlow(ensureLocker(store).equipped_lantern),
    stamp: ensureLocker(store).equipped_stamp,
  };
  board = new Board(canvas, overlay, spec, store.settings, {
    onHud: (h) => {
      const t = document.getElementById("hud-time");
      const s = document.getElementById("hud-score");
      const k = document.getElementById("hud-streak");
      if (t) t.textContent = fmtClock(h.remainingMs);
      if (s) s.textContent = String(h.score);
      if (k) k.textContent = String(h.streak);
      if (h.combo) toast(comboHost, h.combo);
    },
    onComplete: (br, ev) => showResult(spec, br, ev, true),
    onTimeout: (br, ev) => showResult(spec, br, ev, false),
  }, look);

  const pauseMenu = document.getElementById("pause-menu")!;
  document.getElementById("pause")!.onclick = () => {
    board?.pause(true);
    pauseMenu.classList.remove("hidden");
  };
  document.getElementById("resume")!.onclick = () => {
    board?.pause(false);
    pauseMenu.classList.add("hidden");
  };
  document.getElementById("restart")!.onclick = () => {
    pauseMenu.classList.add("hidden");
    paintPlay();
  };
  document.getElementById("quit")!.onclick = () => {
    go(spec.mode === "campaign" ? "campaign" : "home");
  };
  document.getElementById("pm-scout")?.addEventListener("click", () => go("locker"));
  const toggleMute = () => {
    store.settings.mute = !store.settings.mute;
    persist();
    const m = document.getElementById("mute");
    if (m) m.textContent = store.settings.mute ? "Muted" : "Sound";
  };
  document.getElementById("mute")!.onclick = toggleMute;
  document.getElementById("pm-mute")!.onclick = toggleMute;
  document.getElementById("speak")!.onclick = () => {
    const step = spec.steps[board ? (board as Board).stepI : 0];
    if (step) audio.speak(step.problem.speak);
  };
}

function showResult(spec: TrailSpec, br: ScoreBreakdown, events: TrailEvent[], finished: boolean) {
  lastScore = br;
  lastSpec = spec;
  board?.destroy();
  board = null;
  const title = finished ? spec.artTitle : "Trail paused";
  const copy = finished
    ? "The picture is all yours."
    : "Want to try this path again?";
  const breakdown = `
    <ul class="break">
      <li><span>Path points</span><strong>${br.stepPoints}</strong></li>
      <li><span>Time bonus</span><strong>${br.timeBonus}</strong></li>
      <li><span>Perfect trail</span><strong>${br.perfectBonus}</strong></li>
      <li class="total"><span>Total</span><strong>${br.total}</strong></li>
    </ul>`;

  const prevStars =
    spec.mode === "campaign" && spec.level ? getStars(store, spec.grade)[spec.level - 1] || 0 : 0;
  if (finished && spec.mode === "campaign" && spec.level) {
    store = setStar(store, spec.grade, spec.level, br.stars);
    persist();
    void auth.adapter.saveProgress?.(spec.grade, getStars(store, spec.grade));
  }
  const marks = finished ? payoutMarks(store, spec, br, prevStars) : { total: 0, lines: [] };
  const newScouts = finished ? grantDeedUnlocks(store) : [];
  persist();

  const signed = !!user();
  const canPublic = signed && spec.mode !== "practice";

  root().innerHTML = shell(
    "play",
    `<section class="result">
      <div class="result-art${finished ? "" : " is-partial"}" style="background-image:url('assets/illustrations/${spec.art}')"></div>
      <h1>${esc(title)}</h1>
      <p class="lede">${copy}</p>
      ${starsHtml(br.stars)}
      <p class="score-lg">${br.total}</p>
      <p class="fine">${br.stepsDone}/${br.pathLength} steps · ${br.firstTry} first-try · ${br.misses} misses · ${fmtTime(br.timeMs)}</p>
      ${breakdown}
      ${
        finished && marks.total
          ? `<div class="marks-box"><p class="fine">Marks (cosmetic only — score unchanged)</p>
              <ul class="break">${marks.lines.map((l) => `<li><span>${esc(l.label)}</span><strong>+${l.n}</strong></li>`).join("")}
              <li class="total"><span>Marks now</span><strong>${ensureLocker(store).marks}</strong></li></ul>
            </div>`
          : ""
      }
      ${newScouts.length ? `<p class="ok-msg">Scout ready. ${esc(newScouts.join(", "))}</p>` : ""}
      ${classroomLocked(store) ? "" : nextScoutRow()}
      <div id="save-slot"></div>
      <div class="cta-grid">
        <button class="btn btn-primary" id="again">Play again</button>
        ${finished && spec.mode === "campaign" && spec.level && spec.level < 10 ? `<button class="btn btn-warm" id="next">Next trail</button>` : ""}
        <button class="btn" id="map">${spec.mode === "campaign" ? "Campaign map" : "Home"}</button>
        <button class="btn" data-go="leaderboard">Leaderboard</button>
      </div>
    </section>`
  );

  const slot = document.getElementById("save-slot")!;
  if (canPublic) {
    slot.innerHTML = `<button class="btn btn-warm" id="submit">Submit to board</button>`;
    document.getElementById("submit")!.onclick = () => void submitRun(spec, br, events, slot);
  } else if (spec.mode !== "practice") {
    slot.innerHTML = `<div class="save-card">
      <p>Save this score — sign in with your Orangecat account.</p>
      <button class="btn btn-warm" id="to-auth">Sign in to save</button>
      <button class="btn" id="local-save">Keep on this device</button>
    </div>`;
    document.getElementById("to-auth")!.onclick = () => go("account");
    document.getElementById("local-save")!.onclick = () => void submitRun(spec, br, events, slot);
  } else {
    slot.innerHTML = `<p class="fine">Practice stays off the Daily leaderboard.</p>
      <button class="btn" id="local-save">Save practice note</button>`;
    document.getElementById("local-save")!.onclick = () => void submitRun(spec, br, events, slot);
  }

  document.getElementById("again")!.onclick = () => paintPlay();
  document.getElementById("next")?.addEventListener("click", () => {
    playIntent = { mode: "campaign", grade: spec.grade, level: (spec.level || 1) + 1 };
    paintPlay();
  });
  document.getElementById("map")!.onclick = () => go(spec.mode === "campaign" ? "campaign" : "home");
  $("[data-go='leaderboard']")?.addEventListener("click", () => go("leaderboard"));
  root().querySelectorAll<HTMLButtonElement>("[data-next-scout]").forEach((b) => {
    b.onclick = () => {
      const id = b.dataset.nextScout as ScoutId;
      if (!isUnlocked(store, id)) return;
      ensureLocker(store).equipped_scout = id;
      persist();
      root().querySelectorAll("[data-next-scout]").forEach((x) => x.classList.toggle("is-on", x === b));
    };
  });
}

async function submitRun(spec: TrailSpec, br: ScoreBreakdown, events: TrailEvent[], slot: HTMLElement) {
  const payload: SubmitPayload = {
    game: "fact-trail",
    mode: spec.mode,
    grade: spec.grade,
    level: spec.level,
    seed: spec.seed,
    finished: br.finished,
    timeMs: br.timeMs,
    timeLeftMs: Math.round(br.secondsLeft * 1000),
    stars: br.stars,
    misses: br.misses,
    firstTry: br.firstTry,
    stepsDone: br.stepsDone,
    pathLength: br.pathLength,
    events,
    classCode: store.classCode || undefined,
    profileId: store.activeProfileId || undefined,
    nickname: nick(),
    clientScore: br.total,
    scout_id: activeScout(store),
  };
  slot.innerHTML = `<p class="fine">Saving…</p>`;
  const res = await auth.adapter.submitScore(payload);
  if (res.ok) {
    slot.innerHTML = `<p class="ok-msg">Saved${res.rank ? ` · rank ${res.rank}` : ""} · ${res.score ?? br.total} points</p>`;
  } else if (res.needsAuth) {
    slot.innerHTML = `<div class="save-card"><p>${esc(res.error || "Sign in to post on the public board.")}</p>
      <button class="btn btn-warm" id="to-auth">Sign in to save</button></div>`;
    document.getElementById("to-auth")!.onclick = () => go("account");
  } else {
    slot.innerHTML = `<p class="fine">${esc(res.error || "Could not save.")}</p>`;
  }
}

function paintBoard() {
  const grade = store.settings.grade;
  root().innerHTML = shell(
    "leaderboard",
    `<h1>Leaderboard</h1>
    <div class="tabs" id="boards">
      <button class="tab is-on" data-board="daily">Daily</button>
      <button class="tab" data-board="campaign">Campaign</button>
      <button class="tab" data-board="all">All-time</button>
      ${store.classCode ? `<button class="tab" data-board="class">Class</button>` : ""}
    </div>
    <div class="grade-row" id="gpeek">${(Object.keys(GRADE_LABELS) as unknown as string[])
      .map((g) => `<button class="chip${Number(g) === grade ? " is-on" : ""}" data-g="${g}">${GRADE_LABELS[Number(g) as Grade]}</button>`)
      .join("")}</div>
    <div class="table-wrap"><table class="lb"><thead><tr>
      <th>Rank</th><th>Nickname</th><th>Grade</th><th>Score</th><th>Time</th><th>Stars</th><th>Date</th>
    </tr></thead><tbody id="lb-body"><tr><td colspan="7">Loading…</td></tr></tbody></table></div>
    <p class="fine">Nicknames only. Ties: faster time, then fewer misses, then earlier submit.</p>`
  );

  let boardKind: "daily" | "campaign" | "all" | "class" = "daily";
  let g = grade;
  const load = async () => {
    const rows = await auth.adapter.getLeaderboard({
      board: boardKind,
      grade: g,
      classCode: store.classCode,
      dateISO: utcDateISO(),
    });
    const body = document.getElementById("lb-body")!;
    if (!rows.length) {
      body.innerHTML = `<tr><td colspan="7">No scores yet. Be the first on this trail.</td></tr>`;
      return;
    }
    body.innerHTML = rows
      .map(
        (r) => `<tr>
        <td>${r.rank}</td><td>${esc(r.nickname)}</td><td>${GRADE_LABELS[r.grade]}</td>
        <td>${r.score}</td><td>${fmtTime(r.timeMs)}</td><td>${starsHtml(r.stars)}</td><td>${esc(r.date)}</td>
      </tr>`
      )
      .join("");
  };
  root().querySelectorAll<HTMLButtonElement>("#boards .tab").forEach((b) => {
    b.onclick = () => {
      root().querySelectorAll("#boards .tab").forEach((x) => x.classList.remove("is-on"));
      b.classList.add("is-on");
      boardKind = b.dataset.board as any;
      void load();
    };
  });
  root().querySelectorAll<HTMLButtonElement>("#gpeek .chip").forEach((b) => {
    b.onclick = () => {
      root().querySelectorAll("#gpeek .chip").forEach((x) => x.classList.remove("is-on"));
      b.classList.add("is-on");
      g = Number(b.dataset.g) as Grade;
      void load();
    };
  });
  void load();
}

function paintSettings() {
  const s = store.settings;
  const row = (id: string, label: string, on: boolean, hint: string) =>
    `<label class="set-row"><span><strong>${label}</strong><em>${hint}</em></span>
      <input type="checkbox" id="${id}" ${on ? "checked" : ""} /></label>`;
  root().innerHTML = shell(
    "settings",
    `<h1>Settings</h1>
    ${row("mute", "Mute", s.mute, "Turns off plucks, chimes, and read-aloud.")}
    ${row("rm", "Reduced motion", s.reducedMotion, "Skip the walk. Instant reveal.")}
    ${row("hc", "High contrast", s.highContrast, "Stronger ink on cream.")}
    ${row("lg", "Larger type", s.largeType, "Bigger facts and stones.")}
    ${row("dx", "Dyslexia-friendly", s.dyslexia, "Clear letterforms and extra spacing.")}
    ${row("cb", "Color-blind safe stones", s.colorBlind, "Shape + letter + color.")}
    ${row("xt", "Extra +30 seconds", s.extraTime, "Parents/teachers only. Daily Trail stays at default time.")}
    ${row("ra", "Read-aloud (K–2)", s.readAloud, "Web Speech reads the fact. Spacebar repeats.")}
    ${row("sd", "This device is shared", s.sharedDevice, "Show a profile picker on launch.")}
    <div class="cta-grid">
      <button class="btn" data-go="grade">Change grade (now ${GRADE_LABELS[s.grade]})</button>
      <button class="btn" data-go="desk">Parent / teacher desk</button>
    </div>`
  );
  const bind = (id: string, key: keyof Settings) => {
    document.getElementById(id)!.addEventListener("change", (e) => {
      (store.settings as any)[key] = (e.target as HTMLInputElement).checked;
      persist();
    });
  };
  bind("mute", "mute");
  bind("rm", "reducedMotion");
  bind("hc", "highContrast");
  bind("lg", "largeType");
  bind("dx", "dyslexia");
  bind("cb", "colorBlind");
  bind("xt", "extraTime");
  bind("ra", "readAloud");
  bind("sd", "sharedDevice");
  $("[data-go='grade']")?.addEventListener("click", () => go("grade"));
  $("[data-go='desk']")?.addEventListener("click", () => go("desk"));
}

function paintAccount() {
  const u = user();
  const guest = `<div class="auth-box">
      <h1>Account</h1>
      <p class="lede">Register a free Orangecat account to climb the leaderboard. Play is never locked behind a login.</p>
      <p class="fine">Parents and teachers create accounts. Kids under 13 should not type an email. Give your child a nickname and optional PIN on the desk.</p>
      <div class="tabs" id="atabs">
        <button class="tab is-on" data-t="login">Log in</button>
        <button class="tab" data-t="reg">Register</button>
      </div>
      <form id="login-form" class="form">
        <label>Username<input name="u" required maxlength="50" autocomplete="username" /></label>
        <label>Password<input name="p" type="password" required autocomplete="current-password" /></label>
        <p class="auth-msg" id="msg"></p>
        <button class="btn btn-primary" type="submit">Log in</button>
      </form>
      <form id="reg-form" class="form hidden">
        <label>Nickname<input name="u" required maxlength="50" autocomplete="username" /></label>
        <label>Password<input name="p" type="password" required minlength="6" autocomplete="new-password" /></label>
        <label>Email <span class="fine">(optional · parent/teacher)</span><input name="e" type="email" autocomplete="email" /></label>
        <p class="auth-msg" id="msg2"></p>
        <button class="btn btn-primary" type="submit">Create account</button>
      </form>
      <button class="btn btn-ghost" id="guest">Continue as Guest</button>
    </div>`;
  const signed = `<div class="auth-box">
      <h1>Signed in as ${esc(u?.nickname || "")}</h1>
      <p class="lede">Same Orangecat account as Home Run Derby.</p>
      <div class="cta-grid">
        <button class="btn" data-go="desk">Parent / teacher desk</button>
        <button class="btn" data-go="leaderboard">Leaderboard</button>
        <button class="btn" id="out">Log out</button>
      </div>
    </div>`;
  root().innerHTML = shell("account", u ? signed : guest);
  if (u) {
    document.getElementById("out")!.onclick = async () => {
      await auth.adapter.logout();
      auth.user = null;
      paintAccount();
    };
    $("[data-go='desk']")?.addEventListener("click", () => go("desk"));
    $("[data-go='leaderboard']")?.addEventListener("click", () => go("leaderboard"));
    return;
  }
  document.getElementById("atabs")!.onclick = (e) => {
    const t = (e.target as HTMLElement).dataset.t;
    if (!t) return;
    root().querySelectorAll("#atabs .tab").forEach((x) => x.classList.toggle("is-on", (x as HTMLElement).dataset.t === t));
    document.getElementById("login-form")!.classList.toggle("hidden", t !== "login");
    document.getElementById("reg-form")!.classList.toggle("hidden", t !== "reg");
  };
  document.getElementById("login-form")!.onsubmit = async (e) => {
    e.preventDefault();
    const f = e.target as HTMLFormElement;
    const res = await auth.adapter.login(f.u.value, f.p.value);
    const msg = document.getElementById("msg")!;
    if (res.ok && res.user) {
      auth.user = res.user;
      paintAccount();
    } else msg.textContent = res.error || "Could not sign in.";
  };
  document.getElementById("reg-form")!.onsubmit = async (e) => {
    e.preventDefault();
    const f = e.target as HTMLFormElement;
    const res = await auth.adapter.register(f.u.value, f.p.value, f.e.value);
    const msg = document.getElementById("msg2")!;
    if (res.ok && res.user) {
      auth.user = res.user;
      paintAccount();
    } else msg.textContent = res.error || "Could not register.";
  };
  document.getElementById("guest")!.onclick = () => go("home");
}

function firstPickHtml(): string {
  const tiles = SCOUTS.filter((s) => s.unlock.kind === "free")
    .map(
      (s) => `<button type="button" class="scout-tile${s.id === "pip" ? " is-on" : ""}" data-pick="${s.id}">
        <img src="${s.src}" alt="${esc(s.name)}" />
        <strong>${esc(s.name)}</strong>
        <span>${esc(s.blurb)}</span>
      </button>`
    )
    .join("");
  return `<div class="modal" id="first-pick"><div class="modal-card locker-card">
    <h2>Choose your scout</h2>
    <p class="lede">Three are ready. You can change this later in Scouts.</p>
    <div class="scout-grid">${tiles}</div>
    <button class="btn btn-primary" id="pick-go">Trail with Pip</button>
  </div></div>`;
}

function bindFirstPick() {
  const modal = document.getElementById("first-pick");
  if (!modal) return;
  let pick: ScoutId = "pip";
  modal.querySelectorAll<HTMLButtonElement>("[data-pick]").forEach((b) => {
    b.onclick = () => {
      pick = b.dataset.pick as ScoutId;
      modal.querySelectorAll("[data-pick]").forEach((x) => x.classList.toggle("is-on", x === b));
      const go = document.getElementById("pick-go");
      if (go) go.textContent = "Trail with " + scoutById(pick).name;
    };
  });
  document.getElementById("pick-go")!.onclick = () => {
    const L = ensureLocker(store);
    L.equipped_scout = pick;
    L.firstPickDone = true;
    persist();
    modal.remove();
    paintHome();
  };
}

function nextScoutRow(): string {
  const L = ensureLocker(store);
  const opts = SCOUTS.filter((s) => isUnlocked(store, s.id))
    .map(
      (s) => `<button type="button" class="scout-mini${s.id === L.equipped_scout ? " is-on" : ""}" data-next-scout="${s.id}">
        <img src="${s.src}" alt="${esc(s.name)}" /><span>${esc(s.name)}</span>
      </button>`
    )
    .join("");
  return `<div class="next-scout"><p class="fine">Walk the next trail as…</p><div class="scout-row">${opts}</div></div>`;
}

function paintLocker() {
  if (classroomLocked(store)) {
    root().innerHTML = shell(
      "locker",
      `<h1>Scouts</h1><p class="lede">Scouts are paused for class.</p><button class="btn" data-go="home">Home</button>`
    );
    $("[data-go='home']")?.addEventListener("click", () => go("home"));
    return;
  }
  const L = ensureLocker(store);
  const tab = (location.hash.split("?")[1] || "scouts") as "scouts" | "kit" | "deeds";
  const signed = !!user();
  const guestNote =
    !signed && L.guestEarned
      ? `<p class="save-card">Sign in to keep your scouts.</p>`
      : "";

  const scoutTiles = SCOUTS.map((s) => {
    const open = isUnlocked(store, s.id);
    const eq = L.equipped_scout === s.id;
    return `<button type="button" class="scout-tile${eq ? " is-eq" : ""}${open ? "" : " is-lock"}" data-scout="${s.id}" ${open ? "" : "data-locked=1"}>
      <img src="${s.src}" alt="${esc(s.name)}" class="${open ? "" : "sil"}" />
      <strong>${esc(s.name)}</strong>
      ${eq ? `<span class="brass-check" aria-label="equipped">✓</span>` : ""}
      <span class="rule">${open ? esc(s.blurb) : "Not yet. " + esc(s.ruleText)}</span>
    </button>`;
  }).join("");

  const kitBlock = (items: typeof LANTERNS, slot: "lantern" | "pack" | "stamp") =>
    items
      .map((k) => {
        const open = isUnlocked(store, k.id) || k.unlock.kind === "free";
        const eq = (L as any)["equipped_" + slot] === k.id;
        return `<button type="button" class="kit-tile${eq ? " is-eq" : ""}${open ? "" : " is-lock"}" data-kit="${k.id}" data-slot="${slot}">
          <strong>${esc(k.name)}</strong>
          ${eq ? `<span class="brass-check">✓</span>` : ""}
          <span class="rule">${open ? (k.unlock.kind === "free" ? "Ready" : "Unlocked") : "Not yet. " + esc(k.ruleText)}</span>
        </button>`;
      })
      .join("");

  const deeds = [
    ...SCOUTS.filter((s) => s.unlock.kind !== "free"),
    ...LANTERNS.filter((k) => k.unlock.kind !== "free"),
    ...PACKS.filter((k) => k.unlock.kind !== "free"),
    ...STAMPS.filter((k) => k.unlock.kind !== "free"),
  ]
    .map((d) => {
      const open = isUnlocked(store, d.id);
      return `<li class="${open ? "is-done" : ""}"><span>${esc(d.name)}</span><em>${esc(d.ruleText)}</em></li>`;
    })
    .join("");

  const bodyScouts = `<div class="scout-grid">${scoutTiles}</div>
    <div id="preview" class="preview-board" hidden>
      <canvas id="pv" width="220" height="220"></canvas>
      <p class="fine" id="pv-name"></p>
    </div>`;
  const bodyKit = `<h2 class="h2">Lantern</h2><div class="kit-grid">${kitBlock(LANTERNS, "lantern")}</div>
    <h2 class="h2">Pack</h2><div class="kit-grid">${kitBlock(PACKS, "pack")}</div>
    <h2 class="h2">Trail stamp</h2><div class="kit-grid">${kitBlock(STAMPS, "stamp")}</div>`;
  const bodyDeeds = `<ul class="deed-list">${deeds}</ul>`;

  root().innerHTML = shell(
    "locker",
    `<div class="row-between">
      <h1>Scouts</h1>
      <p class="meta-line">${L.marks} Marks</p>
    </div>
    <p class="lede">Cosmetic only. Marks never change score, stars, or rank.</p>
    ${guestNote}
    <div class="tabs">
      <button class="tab${tab === "scouts" ? " is-on" : ""}" data-tab="scouts">Scouts</button>
      <button class="tab${tab === "kit" ? " is-on" : ""}" data-tab="kit">Kit</button>
      <button class="tab${tab === "deeds" ? " is-on" : ""}" data-tab="deeds">Deeds</button>
    </div>
    <div id="locker-body">${tab === "kit" ? bodyKit : tab === "deeds" ? bodyDeeds : bodyScouts}</div>
    <p id="locker-msg" class="fine"></p>
    <button class="btn" data-go="home">Home</button>`
  );

  root().querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) => {
    b.onclick = () => {
      location.hash = "#/locker?" + b.dataset.tab;
      paintLocker();
    };
  });
  $("[data-go='home']")?.addEventListener("click", () => go("home"));

  root().querySelectorAll<HTMLButtonElement>("[data-scout]").forEach((b) => {
    b.onclick = () => {
      const id = b.dataset.scout as ScoutId;
      const def = scoutById(id);
      const msg = document.getElementById("locker-msg")!;
      if (!isUnlocked(store, id)) {
        if (def.unlock.kind === "marks" && canUnlock(store, def.unlock)) {
          const r = buyWithMarks(store, id, def.unlock.n);
          if (r.ok) {
            ensureLocker(store).equipped_scout = id;
            persist();
            msg.textContent = "Scout ready.";
            paintLocker();
            return;
          }
        }
        if (def.unlock.kind === "marksOr" && ensureLocker(store).marks >= def.unlock.n) {
          const r = buyWithMarks(store, id, def.unlock.n);
          if (r.ok) {
            ensureLocker(store).equipped_scout = id;
            persist();
            msg.textContent = "Scout ready.";
            paintLocker();
            return;
          }
        }
        msg.textContent = "Not yet. " + def.ruleText;
        return;
      }
      ensureLocker(store).equipped_scout = id;
      persist();
      paintLocker();
      previewScout(id);
    };
  });

  root().querySelectorAll<HTMLButtonElement>("[data-kit]").forEach((b) => {
    b.onclick = () => {
      const id = b.dataset.kit!;
      const slot = b.dataset.slot as "lantern" | "pack" | "stamp";
      const def = [...LANTERNS, ...PACKS, ...STAMPS].find((k) => k.id === id)!;
      const msg = document.getElementById("locker-msg")!;
      if (!isUnlocked(store, id) && def.unlock.kind !== "free") {
        if (def.unlock.kind === "marks" && canUnlock(store, def.unlock)) {
          const r = buyWithMarks(store, id, def.unlock.n);
          if (r.ok) {
            (ensureLocker(store) as any)["equipped_" + slot] = id;
            persist();
            paintLocker();
            return;
          }
        }
        msg.textContent = "Not yet. " + def.ruleText;
        return;
      }
      (ensureLocker(store) as any)["equipped_" + slot] = id;
      persist();
      paintLocker();
    };
  });
}

function previewScout(id: ScoutId) {
  if (store.settings.reducedMotion) return;
  const wrap = document.getElementById("preview");
  const canvas = document.getElementById("pv") as HTMLCanvasElement | null;
  if (!wrap || !canvas) return;
  wrap.hidden = false;
  document.getElementById("pv-name")!.textContent = scoutById(id).name;
  const img = new Image();
  img.onload = () => {
    const ctx = canvas.getContext("2d")!;
    let t0 = performance.now();
    const draw = (t: number) => {
      const u = (t - t0) / 1000;
      ctx.fillStyle = "#070605";
      ctx.fillRect(0, 0, 220, 220);
      const bob = Math.sin(u * 6) * 4;
      ctx.drawImage(img, 50, 40 + bob, 120, 120);
      if (u < 1) requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);
  };
  img.src = scoutById(id).src;
}

function paintDesk() {
  const list = store.profiles
    .map(
      (p) => `<div class="prof">
        <div><strong>${esc(p.nickname)}</strong> · Grade ${GRADE_LABELS[p.grade]}
          <div class="fine">Last played ${p.lastPlayed ? esc(p.lastPlayed.slice(0, 10)) : "—"}</div>
          <div>${starsHtml((getStars(store, p.grade) || []).reduce((a, b) => a + (b ? 1 : 0), 0) ? 0 : 0)}</div>
        </div>
        <button class="btn" data-use="${p.id}">Play as</button>
      </div>`
    )
    .join("");
  root().innerHTML = shell(
    "desk",
    `<h1>Parent / teacher desk</h1>
    <p class="lede">Private on this device. No public display of this list.</p>
    <section class="card">
      <h2 class="h2">Child profiles</h2>
      <div class="prof-list">${list || "<p class='fine'>No profiles yet.</p>"}</div>
      <form id="add-p" class="form">
        <label>Nickname<input name="n" required maxlength="24" /></label>
        <label>Grade<select name="g">${(Object.keys(GRADE_LABELS) as unknown as string[])
          .map((g) => `<option value="${g}">${GRADE_LABELS[Number(g) as Grade]}</option>`)
          .join("")}</select></label>
        <label>Optional 4-digit PIN<input name="pin" inputmode="numeric" maxlength="4" pattern="[0-9]{4}" /></label>
        <button class="btn btn-primary" type="submit">Add profile</button>
      </form>
    </section>
    <section class="card">
      <h2 class="h2">Classroom lock</h2>
      <label class="set-row"><span><strong>Classroom lock</strong><em>Hides the locker. Everyone on this device uses Pip.</em></span>
        <input type="checkbox" id="clock" ${classroomLocked(store) ? "checked" : ""} />
      </label>
    </section>
    <section class="card">
      <h2 class="h2">Class code</h2>
      <p>Students enter this once. You can reset it anytime.</p>
      <p class="score-lg" id="cc">${esc(store.teacherCode || store.classCode || "————")}</p>
      <div class="mini-row">
        <button class="btn" id="gen">Generate / reset code</button>
        <form id="join" class="inline-form">
          <input name="code" maxlength="6" placeholder="Join a class" aria-label="Class code" />
          <button class="btn" type="submit">Join</button>
        </form>
      </div>
    </section>`
  );
  document.getElementById("add-p")!.onsubmit = (e) => {
    e.preventDefault();
    const f = e.target as HTMLFormElement;
    store.profiles.push({
      id: newId(),
      nickname: f.n.value.trim(),
      grade: Number(f.g.value) as Grade,
      pin: f.pin.value || undefined,
      stars: {},
    });
    persist();
    paintDesk();
  };
  document.getElementById("clock")!.addEventListener("change", (e) => {
    ensureLocker(store).classroomLock = (e.target as HTMLInputElement).checked;
    persist();
    paintDesk();
  });
  document.getElementById("gen")!.onclick = async () => {
    const remote = await auth.adapter.generateClassCode?.();
    store.teacherCode = remote || makeClassCode();
    persist();
    paintDesk();
  };
  document.getElementById("join")!.onsubmit = (e) => {
    e.preventDefault();
    const code = ((e.target as HTMLFormElement).code.value || "").toUpperCase().trim();
    if (code.length === 6) {
      store.classCode = code;
      persist();
      paintDesk();
    }
  };
  root().querySelectorAll<HTMLButtonElement>("[data-use]").forEach((b) => {
    b.onclick = () => {
      const p = store.profiles.find((x) => x.id === b.dataset.use);
      if (!p) return;
      store.activeProfileId = p.id;
      store.settings.grade = p.grade;
      persist();
      go("home");
    };
  });
}

function paintPicker() {
  const cards = store.profiles
    .map((p) => `<button class="btn" data-use="${p.id}">${esc(p.nickname)} · ${GRADE_LABELS[p.grade]}</button>`)
    .join("");
  root().innerHTML = shell(
    "desk",
    `<h1>Who’s playing?</h1>
    <div class="cta-grid">${cards || "<p>Add a profile on the desk.</p>"}</div>
    <button class="btn btn-ghost" data-go="desk">Open desk</button>`
  );
  root().querySelectorAll<HTMLButtonElement>("[data-use]").forEach((b) => {
    b.onclick = () => {
      const p = store.profiles.find((x) => x.id === b.dataset.use);
      if (!p) return;
      if (p.pin) {
        const g = prompt("PIN") || "";
        if (g !== p.pin) return;
      }
      store.activeProfileId = p.id;
      store.settings.grade = p.grade;
      persist();
      go("home");
    };
  });
  $("[data-go='desk']")?.addEventListener("click", () => go("desk"));
}

function $(sel: string): HTMLElement | null {
  return document.querySelector(sel);
}

void start();
